import axios from 'axios';
import { API_BASE_URL, API_PREFIX } from '../utils/constants';

const withApiPrefix = (url) => {
  if (!url || /^https?:\/\//i.test(url)) return url;

  const normalizedUrl = url.startsWith('/') ? url : `/${url}`;
  const normalizedPrefix = API_PREFIX.startsWith('/') ? API_PREFIX : `/${API_PREFIX}`;
  const basePath = (() => {
    try {
      return new URL(API_BASE_URL).pathname || '';
    } catch (e) {
      return '';
    }
  })().replace(/\/+$/, '');
  const baseHasPrefix = basePath.endsWith(normalizedPrefix.replace(/\/+$/, ''));

  // When the base URL already contains the API prefix, avoid adding it again.
  if (baseHasPrefix) {
    // If the incoming path already has the API prefix, strip it so it does not duplicate.
    if (normalizedUrl.startsWith(normalizedPrefix)) {
      return normalizedUrl.slice(normalizedPrefix.length) || '/';
    }

    // If the path starts with /api/, drop that extra segment because the base already has it.
    if (normalizedUrl.startsWith('/api/')) {
      return normalizedUrl.replace(/^\/api(\/|$)/, '/');
    }

    return normalizedUrl;
  }

  if (normalizedUrl.startsWith(normalizedPrefix)) {
    return normalizedUrl;
  }

  // Avoid duplicating the /api segment when API_PREFIX already includes it (e.g. /api/v1.0 + /api/foo)
  if (normalizedUrl.startsWith('/api/')) {
    const trimmedUrl = normalizedUrl.replace(/^\/api(\/|$)/, '/');
    return `${normalizedPrefix}${trimmedUrl}`;
  }

  return `${normalizedPrefix}${normalizedUrl}`;
};

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 8000,
});

let unauthorizedHandler = null;

export const setUnauthorizedHandler = (handler) => {
  unauthorizedHandler = typeof handler === 'function' ? handler : null;
};

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Ensure every relative request is sent under the API version prefix the backend expects
    if (config.url && !(config.meta && config.meta.skipApiPrefix)) {
      config.url = withApiPrefix(config.url);
    }

    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      if (unauthorizedHandler) {
        unauthorizedHandler(error);
      } else {
        localStorage.removeItem('token');
        localStorage.removeItem('session');
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;
